import { AxiosError } from 'axios'
import { createStandardAction } from 'typesafe-actions'
import { AnyAction } from 'redux'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import moment from 'moment'

import { PLANS_URL } from '../../constants/urls/backend'
import { IPlan, TPlanType, INewPlan, IChangedPlan } from '../../types/plan'
import { IRequestResult } from '../types'
import { apiRoute } from '../../utils/routeUtils'
import { axiosWithAdal } from '../../utils/http'

const plansSuccess = createStandardAction('@@plans/SUCCESS')<IPlan[]>()
const plansError = createStandardAction('@@plans/ERROR')<string[]>()
const plansRequest = createStandardAction('@@plans/REQUEST')()

const planDeleteSuccess = createStandardAction('@@plans/DELETE_SUCCESS')<IPlan>()
const planDeleteError = createStandardAction('@@plans/DELETE_ERROR')<string[]>()
const planDeleteRequest = createStandardAction('@@plans/DELETE_REQUEST')()

const planUpdateSuccess = createStandardAction('@@plans/UPDATE_SUCCESS')<IPlan>()
const planUpdateError = createStandardAction('@@plans/UPDATE_ERROR')<string[]>()
const planUpdateRequest = createStandardAction('@@plans/UPDATE_REQUEST')()

const planCreateSuccess = createStandardAction('@@plans/CREATE_SUCCESS')<IPlan>()
const planCreateError = createStandardAction('@@plans/CREATE_ERROR')<string[]>()
const planCreateRequest = createStandardAction('@@plans/CREATE_REQUEST')()

interface IResponse {
  id: number
  created: string
  updated: string
  name: string
  description: string
  drugs: Array<{
    id: number
    module: string
    name: string
    max_course_count: number
  }>
  active_subs: number
  plan_type: TPlanType
}

interface IRequest {
  name: string
  description: string
  drugs: Array<{
    drug: number
    max_course_count: number
  }>
  plan_type: TPlanType
}

const fetchPlans = (): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(plansRequest())
    try {
      const response = await axiosWithAdal.get<IResponse[]>(apiRoute(PLANS_URL), {
        headers: {
          'Content-Type': 'application/json',
        }
      })

      const payload: IPlan[] = response.data.map(x => {
        return {
          id: x.id,
          created: moment(x.created),
          updated: moment(x.updated),
          name: x.name,
          activeSubscriptions: x.active_subs,
          type: x.plan_type,
          description: x.description,
          drugs: x.drugs.map(y => ({id: y.id, module: y.module, name: y.name, maxCourseCount: y.max_course_count}))
        }
      })

      dispatch(plansSuccess(payload))
    } catch (e) {
      dispatch(plansError([ e.message ]))
      return
    }
  }
}

const deletePlan = (plan: IPlan): ThunkAction<Promise<void>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<void> => {
    dispatch(planDeleteRequest())
    try {
      await axiosWithAdal.delete(`${apiRoute(PLANS_URL)}${plan.id}`, {
        headers: {
          'Content-Type': 'application/json',
        }
      })
      dispatch(planDeleteSuccess(plan))

    } catch (e) {
      dispatch(planDeleteError([ e.message ]))
    }
  }
}

const updatePlan = (plan: IChangedPlan): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(planUpdateRequest())
    try {
      const content: IRequest = {
        name: plan.name,
        description: plan.description,
        drugs: plan.drugs.map(x => ({drug: x.id, max_course_count: x.maxCourseCount})),
        plan_type: plan.type,
      }

      const response = await axiosWithAdal.put<IResponse>(
        `${apiRoute(PLANS_URL)}${plan.id}`, JSON.stringify(content), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      dispatch(planUpdateSuccess({
        name: response.data.name,
        description: response.data.description,
        drugs: response.data.drugs.map(x => ({
          id: x.id,
          module: x.module,
          name: x.name,
          maxCourseCount: x.max_course_count
        })),
        type: response.data.plan_type,
        activeSubscriptions: response.data.active_subs,
        id: response.data.id,
        created: moment(response.data.created),
        updated: moment(response.data.updated)
      }))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(planUpdateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

const createPlan = (
  plan: INewPlan
): ThunkAction<Promise<IRequestResult>, {}, {}, AnyAction> => {
  return async (dispatch: ThunkDispatch<{}, {}, AnyAction>): Promise<IRequestResult> => {
    dispatch(planCreateRequest())
    try {
      const content: IRequest = {
        name: plan.name,
        description: plan.description,
        drugs: plan.drugs.map(x => ({drug: x.id, max_course_count: x.maxCourseCount})),
        plan_type: plan.type,
      }

      const response = await axiosWithAdal.post<IResponse>(
        apiRoute(PLANS_URL), JSON.stringify(content), {
        headers: {
          'Content-Type': 'application/json'
        }
      })

      dispatch(planCreateSuccess({
        name: response.data.name,
        description: response.data.description,
        drugs: response.data.drugs.map(x => ({
          id: x.id,
          module: x.module,
          name: x.name,
          maxCourseCount: x.max_course_count
        })),
        type: response.data.plan_type,
        activeSubscriptions: response.data.active_subs,
        id: response.data.id,
        created: moment(response.data.created),
        updated: moment(response.data.updated)
      }))

      return {
        status: response.status,
        message: response.statusText,
      }
    } catch (e) {
      dispatch(planCreateError([ e.message ]))
      const error = e as AxiosError
      return {
        status: error.code ? parseInt(error.code, 10) : 500,
        message: error.message
      }
    }
  }
}

export {
  fetchPlans,
  createPlan,
  updatePlan,
  deletePlan,
  plansSuccess,
  plansRequest,
  plansError,
  planDeleteSuccess,
  planDeleteError,
  planDeleteRequest,
  planCreateSuccess,
  planCreateError,
  planCreateRequest,
  planUpdateSuccess,
  planUpdateError,
  planUpdateRequest,
}
